/**
 * Streama CMS · IAM Zod schemas
 *
 * One Zod schema per backend DTO. Constraints mirror the deployed
 * backend's Bean Validation annotations exactly so client-side
 * validation matches what the server enforces:
 *
 *   UserCreateRequest:    @NotBlank @Size(max=20)  /  @NotBlank @Size(min=8, max=200)
 *   UserUpdateRequest:    optional Boolean / Integer fields
 *   PasswordChangeRequest:@NotBlank /  @NotBlank @Size(min=8, max=200)
 *   PasswordResetRequest: @NotBlank @Size(min=8, max=200)
 *   PermissionsUpdateReq: Map<String, Boolean>
 *
 * Types are derived via z.infer<typeof X> to keep schema and TS type
 * exactly in sync — change the schema, the type follows.
 *
 * Error messages are written for end users, not for developers. They
 * surface in field-level error rendering when client-side validation
 * trips. The backend's fieldErrors take precedence on submit when
 * client validation passes but server rejects.
 */
import { z } from 'zod'

// -----------------------------------------------------------------
// User CRUD
// -----------------------------------------------------------------

export const userCreateSchema = z.object({
  userName: z
    .string()
    .trim()
    .min(1, 'Username is required')
    .max(20, 'Username must be 20 characters or fewer')
    .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, digits, dots, dashes, or underscores'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200, 'Password is too long'),
  external: z.boolean().default(false),
  groupId: z
    .number({ required_error: 'Pick a group' })
    .int()
    .positive('Pick a group'),
})
export type UserCreateInput = z.infer<typeof userCreateSchema>

/**
 * Update schema for the editable fields. Note that userName is NOT
 * here — the backend doesn't allow renames (would orphan login_logs
 * and other FK references). Owner is set by the backend at create
 * time and is also not editable.
 *
 * The five financial integers are nullable because the legacy schema
 * permits NULL for "not configured." Setting one to 0 is different
 * from leaving it null — preserve that distinction.
 */
export const userUpdateSchema = z.object({
  external: z.boolean().optional(),
  tax: z.number().int().nullable().optional(),
  minGuarantee: z.number().int().nullable().optional(),
  withholdingTax: z.number().int().nullable().optional(),
  detailedReport: z.number().int().nullable().optional(),
  approve: z.boolean().nullable().optional(),
})
export type UserUpdateInput = z.infer<typeof userUpdateSchema>

// -----------------------------------------------------------------
// Password operations
// -----------------------------------------------------------------

/**
 * Self-service password change. Both fields required; refinement
 * blocks setting the new password equal to the current one, which
 * would silently no-op on the backend.
 */
export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters')
      .max(200, 'New password is too long'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
  })
export type PasswordChangeFormInput = z.infer<typeof passwordChangeSchema>

/**
 * Backend-side payload for change-my-password — confirm field is
 * client-only and dropped before submit.
 */
export interface PasswordChangeInput {
  currentPassword: string
  newPassword: string
}

/**
 * Admin-resetting-another-user's password. No current-password check
 * because the admin doesn't have it. Confirm field is client-only.
 */
export const passwordResetSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters')
      .max(200, 'New password is too long'),
    confirmPassword: z.string().min(1, 'Please confirm the new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
export type PasswordResetFormInput = z.infer<typeof passwordResetSchema>

/** Backend-side payload — confirm field stripped. */
export interface PasswordResetInput {
  newPassword: string
}

// -----------------------------------------------------------------
// Permissions
// -----------------------------------------------------------------

/**
 * The permissions PATCH submits the full desired state as a Map.
 * No schema validation beyond "must be an object whose values are
 * booleans" — privilege names are open-set (the backend defines them).
 */
export const permissionsUpdateSchema = z.object({
  permissions: z.record(z.string(), z.boolean()),
})
export type PermissionsUpdateInput = z.infer<typeof permissionsUpdateSchema>
